import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BuscasService } from 'src/app/buscas/service/buscas/buscas.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { ModulosBuscaService } from 'src/app/shared/components/filtros-busca/service/modulos-service/modulos.service';
import { HomeService } from '../home-service/home.service';

type OrigemBusca = 'obra' | 'empresa';
type FiltroBusca = 'todas' | 'obra' | 'empresa';

interface BuscaSalva {
  id: number;
  origem: OrigemBusca;
  nome: string;
  moduleID: number | null;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-bloco-buscas-salvas',
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterModule],
  templateUrl: './bloco-buscas-salvas.component.html',
  styleUrls: ['./bloco-buscas-salvas.component.scss'],
})
export class BlocoBuscasSalvasComponent implements OnInit {
  loading = true;
  filtro: FiltroBusca = 'todas';

  private todasBuscas: BuscaSalva[] = [];

  private readonly QTD_EXIBIDA = 7;

  constructor(
    private readonly buscasService: BuscasService,
    private readonly homeService: HomeService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly modulosBuscaService: ModulosBuscaService,
  ) {}

  ngOnInit(): void {
    this.loadSearches();
  }

  get searches(): BuscaSalva[] {
    const lista =
      this.filtro === 'todas'
        ? this.todasBuscas
        : this.todasBuscas.filter((b) => b.origem === this.filtro);

    return lista.slice(0, this.QTD_EXIBIDA);
  }

  get totalEmpresa(): number {
    return this.todasBuscas.filter((b) => b.origem === 'empresa').length;
  }

  get rotaVerTodas(): string {
    return this.filtro === 'empresa' ? '/empresas/salvas' : '/busca/salvas';
  }

  selecionarFiltro(filtro: FiltroBusca): void {
    this.filtro = filtro;
  }

  private loadSearches(): void {
    this.loading = true;

    const userId = this.authService.userID;
    const body = {
      responsibleID: userId ? parseInt(userId, 10) : 0,
      pagination: { page: 1, pageSize: 50, shortcuts: 7, ordenation: '' },
    };

    const usuarioRestrito = this.authService.level === 'Restrito';

    forkJoin({
      obras: this.buscasService.listSavedSearches(body).pipe(catchError(() => of(null))),
      empresas: usuarioRestrito
        ? of(null)
        : this.homeService.listCompanySavedSearches(body).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ obras, empresas }) => {
        const buscasObra: BuscaSalva[] = (obras?.data?.records ?? []).map((s: any) => ({
          id: s.savedSearchesID,
          origem: 'obra' as OrigemBusca,
          nome: s.searchName,
          moduleID: s.moduleID ? Number(s.moduleID) : null,
          createdAt: s.createdAt,
          updatedAt: this.formatDate(s.createdAt),
        }));

        const buscasEmpresa: BuscaSalva[] = (empresas?.data?.records ?? []).map((s: any) => ({
          id: s.savedSearchId,
          origem: 'empresa' as OrigemBusca,
          nome: s.searchName,
          moduleID: s.moduleID ? Number(s.moduleID) : null,
          createdAt: s.createdAt,
          updatedAt: this.formatDate(s.createdAt),
        }));

        this.todasBuscas = [...buscasObra, ...buscasEmpresa].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit',
    });
  }

  abrirBusca(busca: BuscaSalva): void {
    if (busca.origem === 'empresa') {
      this.router.navigate(['/empresas'], {
        queryParams: { savedSearchId: busca.id },
      });
      return;
    }

    if (!busca.moduleID) {
      console.error('Módulo não encontrado!', busca);
      return;
    }

    const rota = this.modulosBuscaService.getRotaById(busca.moduleID);
    if (rota) {
      this.router.navigate(['/buscas', rota], {
        queryParams: { buscaSalvaId: busca.id },
      });
    } else {
      console.error('Rota não encontrada para moduleID:', busca.moduleID);
    }
  }
}